import React, { useMemo, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Flag, History, List, Repeat, SkipForward, Timer, X } from 'lucide-react';
import { ActionBar, Chip, ExerciseStrip, MediaBox, SegmentedProgress, SessionMeta, SyncBadge } from './parts';
import SetLogger from './SetLogger';
import RestTimer from './RestTimer';
import FeedbackPrompt from './FeedbackPrompt';
import { resolveDemo } from './demoRegistry';
import { describeSet, formatClock, formatKg, hasAllSets, muscleNames, nextInGroup, nextSetIndex, plannedSetCount, prescriptionParts, progressOf, supersetOf, trackingKind } from './playerLogic';

// O ecrã de registo do exercício: UM exercício de cada vez, na ordem em que o atleta o faz —
//   1. o exercício em foco no topo, com a imagem grande e «Ver como se faz» sempre à mão;
//   2. a barra de ações logo abaixo (descanso · histórico · substituir);
//   3. o que é preciso saber antes de pegar nos pesos (prescrição, músculos, última vez, sugestão);
//   4. as séries numeradas, com o estado à vista (concluída / a atual com os campos / as planeadas esbatidas);
//   5. a navegação em baixo, com o descanso colado por cima dela.
// Nada aqui inventa estado: mostra o que o servidor tem, o que ficou na fila local e o que o plano prescreveu.

const shortDate = (iso) => { try { return new Date(iso).toLocaleDateString('pt-PT', { day: 'numeric', month: 'short' }); } catch { return ''; } };

export default function ExerciseScreen({
  view, index, onExit, onOpenList, onOpenDemo, onOpenSubstitute, onOpenHistory, onPrev, onNext, onJump, onLog, onEditSet, onSkipRequest, onFinishRequest,
  timer, sync, pendingCount, busy, onFeedback,
}) {
  const exercises = view.exercises;
  const exercise = exercises[index];
  // «Como foi este exercício?»: depois de concluído, um toque e saltável. Saltar é por exercício (não insiste duas vezes).
  const [feedbackDismissed, setFeedbackDismissed] = useState({});
  // O botão de concluir a série vive no cartão (ecrã largo) e na barra de baixo (telemóvel): o SetLogger é o
  // dono do rascunho, e é por esta referência que a barra dispara o mesmo registo.
  const confirmRef = useRef(null);
  const progress = useMemo(() => progressOf(exercises), [exercises]);
  const demo = useMemo(() => resolveDemo(exercise), [exercise]);
  const isLast = index === exercises.length - 1;
  const logged = (exercise.sets || []).length;
  const allDone = hasAllSets(exercise) || exercise.status === 'Completed';
  const kind = trackingKind(exercise.trackingType);
  const primary = muscleNames(exercise.library, 'primary');
  const secondary = muscleNames(exercise.library, 'secondary');
  const equipment = (exercise.library?.equipment || []).filter((e) => e.isPrimary).concat((exercise.library?.equipment || []).filter((e) => !e.isPrimary)).slice(0, 3);
  const parts = prescriptionParts(exercise);
  const last = exercise.lastValues;
  const closed = exercise.status === 'Skipped';
  // A série que está a ser preenchida agora (se há): é ela que a barra de baixo regista.
  const openSet = !closed && exercise.status !== 'Completed' ? nextSetIndex(exercise) : null;
  const nextExercise = exercises[index + 1];
  // A pergunta aparece quando o exercício fica concluído (ou já tem todas as séries previstas) e para quem tem
  // como responder; o «Agora não» fecha-a neste exercício. Nunca bloqueia: a navegação continua sempre disponível.
  const showFeedback = allDone && !closed && !!onFeedback && !feedbackDismissed[exercise.id];
  const neverMarked = exercise.feedbackVerdict === 'never';

  // A barra de ações: só entra o que existe de verdade. O descanso só se oferece num exercício por fazer e quando
  // o plano o prescreve (sem descanso prescrito não há nada para começar); sai enquanto o cronómetro corre — nessa
  // altura os controlos estão no próprio painel do descanso. «Substituir» só enquanto não há séries registadas.
  const restSeconds = exercise.prescription?.restSeconds || 0;
  const stillToDo = !closed && exercise.status !== 'Completed';
  // Num superset não se descansa entre os exercícios do grupo: o descanso é no fim da ronda (no último).
  const group = supersetOf(exercises, index);
  const roundNext = nextInGroup(exercises, index);
  const restsHere = !group || group.isLast;
  const canHistory = !!onOpenHistory && !!(exercise.executedLibraryExerciseId || exercise.library?.id);
  const actions = [
    stillToDo && !timer.active && restSeconds > 0 && restsHere ? {
      key: 'rest', tone: 'rest', icon: Timer, testId: 'start-rest',
      label: group ? 'Descanso entre rondas' : 'Descanso', sub: formatClock(restSeconds), ariaLabel: `Começar o descanso de ${formatClock(restSeconds)}`,
      onClick: () => timer.start(restSeconds),
    } : null,
    canHistory ? {
      key: 'history', icon: History, testId: 'open-history',
      label: 'Histórico', ariaLabel: 'Ver o meu histórico neste exercício', onClick: onOpenHistory,
    } : null,
    !closed && exercise.canSubstitute ? {
      key: 'substitute', icon: Repeat, testId: 'open-substitute',
      label: 'Substituir', ariaLabel: 'Substituir exercício', onClick: onOpenSubstitute, disabled: !!busy?.substitute,
    } : null,
  ];

  const restNextLabel = (() => {
    const open = nextSetIndex(exercise);
    if (open != null) return `Série ${open} de ${plannedSetCount(exercise)}`;
    return nextExercise ? nextExercise.name : 'Fim do treino';
  })();

  return (
    <div className={`wp-screen wp-player ${timer.active ? 'has-rest' : ''}`} data-testid="exercise-screen">
      <header className="wp-player-top">
        <button type="button" className="wp-icon-btn" onClick={onExit} aria-label="Sair do treino (fica em curso)" data-testid="exit-player"><X size={22} aria-hidden="true" /></button>
        <SegmentedProgress exercises={exercises} index={index} onJump={onJump} />
        <button type="button" className="wp-icon-btn" onClick={onOpenList} aria-label="Lista de exercícios" data-testid="open-list"><List size={22} aria-hidden="true" /></button>
      </header>

      <div className="wp-player-sub">
        <span className="wp-position" data-testid="position">Exercício {index + 1} de {exercises.length}<span className="wp-dim wp-position-name"> · {exercise.name}</span></span>
        <SessionMeta progress={progress} startedAt={view.startedAt} />
        <SyncBadge sync={sync} pending={pendingCount} />
      </div>

      {/* A faixa dos exercícios: o dia inteiro à vista, com o estado de cada um e um toque para saltar. */}
      <ExerciseStrip exercises={exercises} index={index} onJump={onJump} />

      <div className="wp-player-body">
        <div className="wp-player-left">
          {/* 1. O exercício em foco: imagem grande, e a demonstração a um toque. */}
          <MediaBox demo={demo} name={exercise.name} groupLabel={exercise.library?.groupLabel} onOpenDemo={onOpenDemo} />

          {/* 2. A barra de ações, logo abaixo da imagem. */}
          <ActionBar items={actions} />
          {roundNext ? (
            <p className="wp-note wp-superset-note" role="status" data-testid="superset-no-rest">
              {group.label} → a seguir <strong>{group.label.replace(/\d+$/, '')}{group.position + 1}</strong> {roundNext.name}, sem descanso. O descanso {formatClock(restSeconds)} é no fim da ronda.
            </p>
          ) : null}

          {/* 3. O que é preciso saber antes de fazer a série. */}
          <div className="wp-ex-head">
            <h1 className="wp-ex-title" data-testid="exercise-name">
              {group ? <span className="wp-group-badge" data-testid="superset-label">{group.label}</span> : null}
              {exercise.name}
            </h1>
            {exercise.wasSubstituted ? (
              <p className="wp-ex-planned"><Repeat size={14} aria-hidden="true" /> Em vez de {exercise.plannedName}{exercise.substitutionReason ? ` · ${exercise.substitutionReason}` : ''}</p>
            ) : null}
          </div>

          {/* O que explica a escolha (prescrição, músculos, sugestão, última vez, notas). No telemóvel vem DEPOIS
              das séries: primeiro faz-se a série, só depois se lê o contexto — é o que o atleta precisa ao abrir. */}
          <div className="wp-ex-info">

            <dl className="wp-rx" aria-label="Prescrição">
              {parts.map((p) => (
                <div key={p.key} className="wp-rx-item"><dt>{p.label}</dt><dd>{p.value}</dd></div>
              ))}
              {parts.length === 0 ? <div className="wp-rx-item"><dt>Prescrição</dt><dd>Livre</dd></div> : null}
            </dl>

            {primary.length > 0 || equipment.length > 0 || exercise.deloaded ? (
              <div className="wp-chip-row wp-ex-chips">
                {primary.slice(0, 3).map((m) => <Chip key={m} tone="accent">{m}</Chip>)}
                {secondary.slice(0, 2).map((m) => <Chip key={m}>{m}</Chip>)}
                {equipment.map((e) => <Chip key={e.id} tone="muted">{e.name}</Chip>)}
                {exercise.deloaded ? <Chip tone="accent">Semana leve</Chip> : null}
              </div>
            ) : null}

            {exercise.deloaded ? (
              <p className="wp-note wp-deload-note" data-testid="deload-exercise">
                Semana leve: {view.deload?.reduction || 'séries e carga reduzidas'}.
              </p>
            ) : null}

            {exercise.notes ? <p className="wp-ex-notes">💬 {exercise.notes}</p> : null}

            {/* A sugestão do servidor (progressão, recorde a bater) é o que decide a carga: fica à vista em todos os ecrãs. */}
            {exercise.suggestion?.text ? (
              <p className="wp-suggestion" data-testid="suggestion" data-kind={exercise.suggestion.kind}>
                <span className="wp-last-label">Sugestão{exercise.suggestion.basedOn ? ` · com base em ${shortDate(exercise.suggestion.basedOn)}` : ''}</span>
                {exercise.suggestion.text}
              </p>
            ) : null}

            {last ? (
              <p className="wp-last" data-testid="last-session">
                <span className="wp-last-label">Última sessão · {shortDate(last.performedAt)}</span>
                {last.sets.map((s) => describeSet(s, exercise.trackingType)).join('  ·  ')}{kind.usesTime ? '' : ' reps'}
                {last.topWeightKg ? <span className="wp-dim"> · máx. {formatKg(last.topWeightKg)} kg</span> : null}
              </p>
            ) : <p className="wp-last wp-dim" data-testid="last-session">Primeira vez com este exercício.</p>}

            {/* «Nunca mais»: este exercício não volta a ser proposto. Se ainda estiver no treino de hoje, diz-se e dá-se a troca. */}
            {neverMarked ? (
              <p className="wp-feedback-note" role="status" data-testid="feedback-never-note">
                <Repeat size={14} aria-hidden="true" /> Marcado «Nunca mais»: não volta a ser proposto nos próximos treinos.
                {exercise.canSubstitute ? ' Se não o queres fazer agora, troca por outro.' : ''}
              </p>
            ) : null}
          </div>
        </div>

        <div className="wp-player-right">
          {/* 4. As séries: numeradas, com o estado à vista e a série atual pronta a editar. */}
          {closed ? (
            <div className="wp-skipped" role="status"><SkipForward size={22} aria-hidden="true" /> Exercício saltado.</div>
          ) : (
            <SetLogger exercise={exercise} onLog={onLog} onEditSet={onEditSet} disabled={busy?.log} confirmRef={confirmRef} />
          )}

          {!closed && logged === 0 && exercise.status !== 'Completed' ? (
            <div className="wp-secondary">
              <button type="button" className="wp-btn wp-btn-ghost" onClick={onSkipRequest} data-testid="skip-exercise"><SkipForward size={18} aria-hidden="true" /> Saltar este exercício</button>
            </div>
          ) : null}
        </div>
      </div>

      <RestTimer timer={timer} nextLabel={restNextLabel} />

      {/* Não interrompe: fica acima da navegação e o «Seguinte»/«Terminar treino» continuam a funcionar sem responder. */}
      {showFeedback ? (
        <FeedbackPrompt
          exercise={exercise}
          value={exercise.feedbackVerdict}
          busy={!!busy?.feedback}
          onSend={(verdict) => onFeedback(verdict)}
          onDismiss={() => setFeedbackDismissed((d) => ({ ...d, [exercise.id]: true }))}
        />
      ) : null}

      {/* O que vem a seguir, sempre à vista: sem abrir a lista, sem tocar em nada. */}
      <div className="wp-nextup" data-testid="next-up">
        {isLast ? <span className="wp-dim">Último exercício do treino</span> : <><span className="wp-dim">A seguir:</span> <strong>{nextExercise?.name}</strong>{nextExercise ? <span className="wp-dim"> · {plannedSetCount(nextExercise)} séries</span> : null}</>}
      </div>

      <div className="wp-player-nav">
        <button type="button" className="wp-btn wp-btn-ghost wp-nav-prev" onClick={onPrev} disabled={index === 0} aria-label="Exercício anterior" data-testid="prev-exercise"><ChevronLeft size={22} aria-hidden="true" /> <span>Anterior</span></button>
        {openSet != null ? (
          // A ação do momento: registar a série. Ocupa o meio da barra — o polegar chega-lhe sempre e não flutua
          // por cima do cartão. A navegação continua nos dois lados (e na faixa dos exercícios, em cima).
          <button type="button" className="wp-btn wp-btn-primary wp-nav-confirm" onClick={() => confirmRef.current?.()} disabled={busy?.log} data-testid="confirm-set-nav">
            <Check size={22} strokeWidth={3} aria-hidden="true" /> <span>Concluir série {openSet}</span>
          </button>
        ) : null}
        {isLast ? (
          <button type="button" className={`wp-btn wp-nav-next ${progress.setsLogged > 0 ? 'wp-btn-primary' : 'wp-btn-ghost'}`} onClick={onFinishRequest} disabled={progress.setsLogged === 0} data-testid="finish-workout" aria-label="Terminar treino">
            <Flag size={20} aria-hidden="true" /> <span>Terminar treino</span>
          </button>
        ) : (
          <button type="button" className={`wp-btn wp-nav-next ${allDone ? 'wp-btn-primary' : 'wp-btn-ghost'}`} onClick={onNext} data-testid="next-exercise" aria-label={`Exercício seguinte: ${nextExercise?.name || ''}`}>
            <span>{allDone ? 'Seguinte' : 'Avançar'}</span> <ChevronRight size={22} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}
